import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COLORES, COLOR_POR_DEFECTO, MAX_ETIQUETAS, MAX_NOMBRE, cambiarCatalogo } from '../../utils/etiquetasDeChat';

/**
 * CREAR, RENOMBRAR, CAMBIAR DE COLOR Y BORRAR las etiquetas de la organización (punto 11). Sólo lo abre quien
 * `puedeGestionarEtiquetas` (owner y admin); el callable lo vuelve a comprobar. Antes de llamar, la MISMA función
 * del servidor (`cambiarCatalogo`) dice si el pedido vale, para avisar sin ir y volver; el servidor decide sobre lo
 * guardado (el tope y los repetidos, en su transacción).
 */
const llamar = (datos) => httpsCallable(getFunctions(), 'gestionarEtiquetasDeChat')(datos);

function SelectorDeColor({ valor, onCambiar, deshabilitado, t }) {
  return (
    <div role="radiogroup" aria-label={t('labels.color')} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
      {Object.entries(COLORES).map(([nombre, hex]) => (
        <button
          key={nombre}
          type="button"
          role="radio"
          aria-checked={valor === nombre}
          aria-label={t(`labels.colors.${nombre}`)}
          title={t(`labels.colors.${nombre}`)}
          disabled={deshabilitado}
          onClick={() => onCambiar(nombre)}
          style={{
            width: 22, height: 22, borderRadius: '50%', backgroundColor: hex, cursor: 'pointer', padding: 0,
            border: valor === nombre ? '3px solid #1a202c' : '2px solid #fff', boxShadow: '0 0 0 1px #cbd5e0',
          }}
        />
      ))}
    </div>
  );
}

function FilaDeEtiqueta({ etiqueta, catalogo, organizationId, t }) {
  const [nombre, setNombre] = useState(etiqueta.nombre);
  const [color, setColor] = useState(etiqueta.color);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const cambiada = nombre !== etiqueta.nombre || color !== etiqueta.color;

  const enviar = async (pedido) => {
    const previa = cambiarCatalogo(catalogo, pedido);
    if (!previa.ok) { setError(t(`labels.errors.${previa.error}`)); return; }
    setOcupado(true); setError(null);
    try {
      await llamar({ organizationId, ...pedido });
    } catch (e) {
      setError(t(`labels.errors.${e?.details?.motivo || 'generico'}`, { defaultValue: t('labels.errors.generico') }));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <li style={{ padding: '0.6rem 0', borderBottom: '1px solid #edf2f7', listStyle: 'none' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        <input
          value={nombre}
          maxLength={MAX_NOMBRE}
          onChange={(e) => setNombre(e.target.value)}
          aria-label={t('labels.name')}
          disabled={ocupado}
          style={{ flex: '1 1 10rem', minWidth: 0, padding: '0.35rem 0.5rem', border: '1px solid #cbd5e0', borderRadius: 6 }}
        />
        <SelectorDeColor valor={color} onCambiar={setColor} deshabilitado={ocupado} t={t} />
        <button type="button" disabled={!cambiada || ocupado} onClick={() => enviar({ accion: 'editar', id: etiqueta.id, nombre, color })}>
          {t('labels.save')}
        </button>
        {!confirmarBorrado ? (
          <button type="button" disabled={ocupado} onClick={() => setConfirmarBorrado(true)} style={{ color: '#c53030' }}>
            {t('labels.delete')}
          </button>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: 13 }}>
            {t('labels.confirmDelete')}
            <button type="button" disabled={ocupado} onClick={() => enviar({ accion: 'borrar', id: etiqueta.id })} style={{ color: '#c53030', fontWeight: 600 }}>
              {t('labels.delete')}
            </button>
            <button type="button" disabled={ocupado} onClick={() => setConfirmarBorrado(false)}>{t('labels.cancel')}</button>
          </span>
        )}
      </div>
      {error && <div role="alert" style={{ color: '#c53030', fontSize: 13, marginTop: 4 }}>{error}</div>}
    </li>
  );
}

export default function GestorDeEtiquetas({ catalogo, organizationId, onCerrar }) {
  const { t } = useTranslation('chats');
  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState(COLOR_POR_DEFECTO);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);
  const lleno = catalogo.length >= MAX_ETIQUETAS;

  const crear = async (e) => {
    e.preventDefault();
    const previa = cambiarCatalogo(catalogo, { accion: 'crear', nombre, color }, { nuevoId: () => 'nueva' });
    if (!previa.ok) { setError(t(`labels.errors.${previa.error}`)); return; }
    setOcupado(true); setError(null);
    try {
      await llamar({ organizationId, accion: 'crear', nombre, color });
      setNombre('');
    } catch (err) {
      setError(t(`labels.errors.${err?.details?.motivo || 'generico'}`, { defaultValue: t('labels.errors.generico') }));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('labels.manageTitle')}
      onClick={(e) => { if (e.target === e.currentTarget) onCerrar(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <h3 style={{ margin: 0 }}>{t('labels.manageTitle')}</h3>
          <button type="button" onClick={onCerrar} aria-label={t('labels.close')}>✕</button>
        </div>
        <p style={{ color: '#4a5568', fontSize: 13 }}>{t('labels.manageHelp', { usadas: catalogo.length, max: MAX_ETIQUETAS })}</p>

        <form onSubmit={crear} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <input
            value={nombre}
            maxLength={MAX_NOMBRE}
            onChange={(e) => setNombre(e.target.value)}
            placeholder={t('labels.newPlaceholder')}
            aria-label={t('labels.name')}
            disabled={ocupado || lleno}
            style={{ flex: '1 1 10rem', minWidth: 0, padding: '0.35rem 0.5rem', border: '1px solid #cbd5e0', borderRadius: 6 }}
          />
          <SelectorDeColor valor={color} onCambiar={setColor} deshabilitado={ocupado || lleno} t={t} />
          <button type="submit" disabled={ocupado || lleno || !nombre.trim()}>{t('labels.create')}</button>
        </form>
        {lleno && <div style={{ color: '#975a16', fontSize: 13 }}>{t('labels.errors.tope')}</div>}
        {error && <div role="alert" style={{ color: '#c53030', fontSize: 13 }}>{error}</div>}

        <ul style={{ padding: 0, margin: '0.5rem 0 0' }}>
          {catalogo.map((e) => (
            // La clave lleva nombre y color: si otro owner la cambia, la fila arranca con lo guardado.
            <FilaDeEtiqueta key={`${e.id}:${e.nombre}:${e.color}`} etiqueta={e} catalogo={catalogo} organizationId={organizationId} t={t} />
          ))}
        </ul>
        {!catalogo.length && <p style={{ color: '#718096', fontSize: 13 }}>{t('labels.empty')}</p>}
      </div>
    </div>
  );
}
